import styled from "@emotion/styled";
import { Container, Link } from "atoms";
import { preventForwarding } from "styles/styleUtils";
import { paths } from "utils/paths";

type TabLink = {
  label: string;
  to: (id: string) => string;
};

const tablinks: TabLink[] = [
  { label: "Featured", to: paths.artistProfile },
  { label: "Portfolio", to: paths.artistPortfolio },
  { label: "Updates", to: paths.artistUpdates },
  { label: "Reviews", to: paths.artistReviews },
  { label: "About", to: paths.artistAbout },
];

type ArtistProfileTabsProps = {
  pathname: string;
  artistId: string;
};

function ArtistProfileTabs({ pathname, artistId }: ArtistProfileTabsProps) {
  return (
    <TabsWrapper>
      <Container width="lg">
        <TabRow aria-label="Artist Profile">
          {tablinks.map(({ label, to }) => (
            <ArtistTabLink
              key={label}
              to={to(artistId)}
              isActive={
                label === "Featured"
                  ? pathname === to(artistId)
                  : pathname.startsWith(to(artistId))
              }
            >
              {label}
            </ArtistTabLink>
          ))}
        </TabRow>
      </Container>
    </TabsWrapper>
  );
}

const TabsWrapper = styled.div(({ theme }) => ({
  position: "relative",
  marginBottom: theme.spacing.lg,
}));
const TabRow = styled.nav(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexWrap: "nowrap",
  maxWidth: `calc(100% + (${theme.spacing.gutter} * 2))`,
  margin: `calc(${theme.spacing.gutter} * -1)`,
  padding: `${theme.spacing.sm} 0`,
  overflowX: "auto",
  overflowY: "visible",
  scrollbarColor: "rgba(0,0,0,.2) rgba(0,0,0,0)",
  scrollbarWidth: "none",
  gap: "2.5vw",
  "@media (min-width: 350px)": {
    gap: "3vw",
  },
  "@media (min-width: 380px)": {
    gap: "4vw",
  },
  [theme.breakpoints.tablet]: {
    justifyContent: "flex-start",
    gap: 10,
    margin: 0,
    maxWidth: "100%",
    marginLeft: `calc(${theme.spacing.xs} * -1)`,
  },
  "&::after": {
    content: "''",
    display: "block",
    position: "absolute",
    bottom: `calc(${theme.spacing.sm} - 1px)`,
    height: 1,
    left: 0,
    right: 0,
    zIndex: 1,
    backgroundColor: theme.colors.fg20,
  },
  "&::WebkitScrollbar": {
    display: "none",
    height: "4px",
  },
  "&::WebkitScrollbar-thumb": {
    backgroundColor: "rgba(0,0,0,.2)",
    borderLeft: `${theme.spacing.gutter} solid white`,
    borderRight: `${theme.spacing.gutter} solid white`,
    height: "6px",
  },
}));
const ArtistTabLink = styled(Link, {
  shouldForwardProp: preventForwarding("isActive"),
})<{ isActive: boolean }>(({ theme, isActive }) => ({
  ...theme.fonts.display,
  fontSize: 14,
  color: isActive ? theme.colors.fg : theme.colors.fg70,
  textTransform: "uppercase",
  textDecoration: "none",
  lineHeight: 1,
  paddingTop: theme.spacing.md,
  paddingBottom: theme.spacing.sm,
  display: "inline-block",
  position: "relative",
  transform: "translateY(2px)",
  transition: ".3s ease color",
  zIndex: 2,
  "@media (min-width: 350px)": {
    fontSize: 15,
  },
  [theme.breakpoints.xs]: {
    fontSize: theme.fontSizes.sm,
  },
  [theme.breakpoints.tablet]: {
    paddingLeft: theme.spacing.xs,
    paddingRight: theme.spacing.xs,
  },
  "&:hover, &:focus, &:focus-visible": {
    color: theme.colors.fg,
  },
  "&::after": {
    content: "''",
    display: "block",
    position: "absolute",
    bottom: 0,
    height: 3,
    width: "100%",
    backgroundColor: theme.colors.fg,
    borderRadius: theme.borderRadius.round,
    opacity: isActive ? 1 : 0,
    transition: ".3s ease opacity",
    [theme.breakpoints.tablet]: {
      width: `calc(100% - (${theme.spacing.xs} * 2))`,
    },
  },
}));

export { ArtistProfileTabs };
